import React, { useContext, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
import { EventContext, TimeContext, firebase } from "Components";
import {
  PreShow,
  Show,
  PostShow,
  Register,
  AdminControls,
  Preview,
} from "./Components";

const adminViews = ["register", "preview", "preshow", "show", "postshow"];

export const Root = () => {
  const { event } = useContext(EventContext);
  const { time } = useContext(TimeContext);
  const params = new URLSearchParams(useLocation().search);
  const [loading, setLoading] = useState(true);
  const [cookies, setCookie] = useCookies();
  const [adminView, setAdminView] = useState(null);

  const handleRegister = useCallback(
    (isAdmin, token, formPart = null) => {
      // registration cookie for two-part form
      if (formPart === 1) {
        setCookie("registration", "incomplete", { maxAge: 31622400 });
      } else if (formPart === 2) {
        setCookie("registration", "complete", { maxAge: 31622400 });
      } else {
        setCookie("registration", null, { maxAge: 31622400 });
      }

      setCookie("token", token, { maxAge: 31622400 });

      if (isAdmin) {
        setCookie("admin", true, { maxAge: 31622400 });
        window.location.reload();
      }
    },
    [setCookie]
  );

  useEffect(() => {
    (async () => {
      if (params.get("token") && !cookies.token) {
        const verify = firebase.functions().httpsCallable("auth-verify");
        try {
          const verified = await verify({
            eventId: process.env.REACT_APP_EVENT_ID,
            token: params.get("token"),
          });
          if (verified?.data?.success) {
            handleRegister(verified?.data?.admin, params.get("token"), 2);
          }
        } catch (error) {
          console.error("token validation failed");
        }
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return null;

  if (!event.preview) {
    delete adminViews[1];
  }

  const register = {
    ...event.register,
    start: event.start,
    artist: event.artist,
    title: event.title,
    mastercampaignId: event.mastercampaignId,
    altLogo: event.logos?.header,
    overlayImage: event.logos?.footer,
    ...event,
  };

  const getAdminView = () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const viewHistory = urlParams.get("view");

    switch (viewHistory || adminView) {
      case "register":
        return (
          <Register
            onSuccess={handleRegister}
            event={register}
            cookies={cookies}
          />
        );
      case "preshow":
        return <PreShow event={event} />;
      case "show":
        return <Show event={event} token={cookies.token} />;
      case "postshow":
        return <PostShow event={event} token={cookies.token} />;
      case "preview":
        return <Preview event={event.preview} />;
      default:
        return null;
    }
  };

  if (cookies?.admin) {
    return (
      <>
        <AdminControls
          views={adminViews}
          selectedView={adminView}
          setAdminView={setAdminView}
        />
        {getAdminView()}
      </>
    );
  }

  const preshowTransitionMinutes = event.preshowTransitionMinutes || 30;

  if (event.end < time && event.campaignEnd > time) {
    return <PostShow event={event} token={cookies.token} />;
  }

  if (
    cookies?.registration !== "complete" ||
    time < event.countdown ||
    event.campaignEnd < time
  ) {
    return (
      <Register onSuccess={handleRegister} event={register} cookies={cookies} />
    );
  }

  if (
    event.countdown > time &&
    !!event.preview &&
    event.start < time.plus({ minutes: preshowTransitionMinutes })
  ) {
    return <Preview event={event.preview} />;
  }

  if (
    event.start > time.plus({ minutes: preshowTransitionMinutes }) &&
    time < event.start
  ) {
    return <PreShow event={event} />;
  }

  return <Show event={event} token={cookies.token} />;
};
